import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Card, CardBody, CardTitle, Row, Col, FormGroup, Label, Input,
    Button, Alert, Spinner, Table, Collapse, Badge,
    Modal,
    ModalHeader,
    ModalBody
} from "reactstrap";
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate, useParams } from 'react-router-dom';
import * as dayjs from 'dayjs';
import InvoiceDetailForm from "./InvoiceDetailForm";
import { getFinanceFilters, saveFinanceFilter } from "./financeFilterStorage";
import SearchableDropdownFilter from "../SearchableDropdownFilter";

const getCustomerLabel = (customer) => customer.name;
const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;
const getOptionId = (option) => option.id;

const InvoiceAudit = ({ readOnly = false }) => {
    const navigate = useNavigate();
    const { invoiceId } = useParams();
    const savedFilters = getFinanceFilters();
    const customersApi = "/api/customers";
    const subCustomersApi = (customerId) => `/api/subcustomers/byCustomer/${customerId}`;
    const loadApi = (customerId, endingDate) => `/api/invoicing/for-customer?customerId=${customerId}&endingDate=${endingDate}&type=submitted`;
    const loadInvoiceApi = (id) => `/api/invoicing/for-invoice/${id}`;
    const routeWeeksApi = "/api/routes/invoice-weeks";
    const approveApi = (invoiceId, push) => `/api/invoicing/${invoiceId}/approve?pushToQuickBooks=${push ? "true" : "false"}`;
    const approveOverrideApi = (invoiceId, push) => `/api/invoicing/${invoiceId}/approve-override?pushToQuickBooks=${push ? "true" : "false"}`;
    const submitApi = (invoiceId) => `/api/invoicing/${invoiceId}/submit/false`;
    const [customers, setCustomers] = useState([]);
    const [subCustomers, setSubCustomers] = useState([]);
    const [subCustomerId, setSubCustomerId] = useState(savedFilters.subCustomerId || "");
    const [customerId, setCustomerId] = useState(savedFilters.customerId || "");
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [error, setError] = useState("");
    const [weeks, setWeeks] = useState([
        { endingDate: '', displayDate: '' }
    ]);
    const [selectedWeek, setSelectedWeek] = useState(savedFilters.selectedWeek || '');
    const [submitOpen, setSubmitOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideReason, setOverrideReason] = useState("");
    const [overrideSubmitting, setOverrideSubmitting] = useState(false);
    // In-flight guard for Approve / Send-to-Review so a slow QBO push can't be double-submitted
    // (which previously created duplicate QuickBooks invoices).
    const [approveSubmitting, setApproveSubmitting] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesText, setNotesText] = useState("");
    const [editing, setEditing] = useState(null);
    const [pushToQbo, setPushToQbo] = useState(true);
    const [qboPreview, setQboPreview] = useState(null);
    const [qboPreviewOpen, setQboPreviewOpen] = useState(false);
    const [qboPreviewBlocked, setQboPreviewBlocked] = useState(false);

    useEffect(() => {
        if (readOnly) return;
        const loadData = async () => {
            const token = await Session.getToken();
            fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setWeeks).catch(() => setWeeks([]));
            fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setCustomers).catch(() => setCustomers([]));
        };
        loadData();
    }, [readOnly]);

    // Read-only single-invoice load (approved-invoice report "View" link). Fetches just this
    // invoice's detail lines; the filter-driven load below is skipped when readOnly.
    useEffect(() => {
        if (!readOnly || !invoiceId) return;
        const run = async () => {
            setLoading(true); setError("");
            try {
                const token = await Session.getToken();
                const res = await fetch(loadInvoiceApi(invoiceId), { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const data = await res.json();
                setSchedule(data.schedule);
                setInvoices(data.invoices || []);
            } catch (e) {
                setError(e.message || "Failed to load invoice.");
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [readOnly, invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
    const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Fuel-surcharge rates are per-mile and need 3 decimals to read accurately (e.g. 0.119).
    const rateMoney = (detail) => {
        const digits = isFuelSurchargeLine(detail) ? 3 : 2;
        return (detail?.billRate ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    };
    const roundMoney = (val) => Math.round((Number(val ?? 0) + Number.EPSILON) * 100) / 100;
    const isAdjustmentLine = (detail) => detail?.surchargeId == null && detail?.transactionTypeId != null;
    const isOvertimeLine = (detail) => detail?.surchargeId === null && Number(detail?.expectedDays ?? 0) === 0 && Number(detail?.overtimeHours ?? 0) > 0;
    const getOvertimeCutoff = (route) => {
        if (!route) return 0;
        if (route.billingType === "Half-day") return Number(route.halfDayOT ?? 0);
        return Number(route.fullDayOT ?? 0);
    };
    const isOvertimeCutoffValidationLine = (detail) => detail?.surchargeId === null
        && detail?.route?.allowOvertime === true
        && getOvertimeCutoff(detail.route) <= 0
        && Number(detail?.expectedDays ?? 0) === 0
        && Number(detail?.overtimeHours ?? 0) === 0
        && Number(detail?.billRate ?? 0) === 0;
    const isFuelSurchargeLine = (detail) => Number(detail?.surcharge?.surchargeTypeId ?? 0) === 5
        || `${detail?.surcharge?.surchargeType?.name ?? ""}`.toLowerCase().includes("fuel");
    const isPercentageSurchargeLine = (detail) => Number(detail?.surcharge?.surchargeTypeId ?? 0) === 2
        || `${detail?.surcharge?.surchargeType?.name ?? ""}`.toLowerCase() === "percentage";
    const isFuelSurchargeValidationLine = (detail) => detail?.hasValidationError === true
        && isFuelSurchargeLine(detail);
    const isZeroCalculatedFuelSurchargeLine = (detail) => isFuelSurchargeLine(detail)
        && Number(detail?.billRate ?? 0) <= 0
        && Number(detail?.lineTotal ?? 0) <= 0;
    // A "False Trip" line shares its route's routeId. It may be the auto-generated flat line
    // (description "False Trip", no transaction type) or a manually added / classification-sourced
    // line carrying a "False Trip" transaction type; recognize both so the label and QBO item stay
    // tied to the route.
    const isFalseTripLine = (detail) => detail?.routeId != null && detail?.surchargeId == null
        && (`${detail?.description ?? ""}`.trim().toLowerCase().startsWith("false trip")
            || `${detail?.transactionType?.transactionName ?? ""}`.toLowerCase().includes("false trip"));
    // The route-less "Backup Driver" standby line — no route/surcharge/transaction type; identified
    // by its description.
    const isBackupDriverLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
        && detail?.transactionTypeId == null
        && `${detail?.description ?? ""}`.trim().toLowerCase() === "backup driver";
    // The route-less backup "make-up" line — no route/surcharge/transaction type; identified by its
    // description.
    const isBackupMakeupLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
        && detail?.transactionTypeId == null
        && `${detail?.description ?? ""}`.trim().toLowerCase() === "backup make-up";
    // The route-less "Lead Pay" management line — no route/surcharge/transaction type; identified
    // by its description.
    const isLeadDriverLine = (detail) => detail?.routeId == null && detail?.surchargeId == null
        && detail?.transactionTypeId == null
        && `${detail?.description ?? ""}`.trim().toLowerCase() === "lead pay";
    const isBaseLine = (detail) => detail?.surchargeId === null && detail?.routeId != null && !isAdjustmentLine(detail) && !isFalseTripLine(detail) && !isBackupDriverLine(detail) && !isBackupMakeupLine(detail) && !isLeadDriverLine(detail) && !isOvertimeLine(detail) && !isOvertimeCutoffValidationLine(detail);
    const isHourlyBaseLine = (detail) => isBaseLine(detail) && detail?.route?.billingType === "Hourly";
    const lineDescription = (detail) => isFalseTripLine(detail)
        ? "False Trip"
        : isAdjustmentLine(detail)
        ? (detail.description || detail.transactionType?.transactionName || "Adjustment")
        : isBackupDriverLine(detail)
        ? "Backup Driver"
        : isBackupMakeupLine(detail)
        ? "Backup Driver"
        : isLeadDriverLine(detail)
        ? "Lead Pay"
        : isFuelSurchargeValidationLine(detail) || isZeroCalculatedFuelSurchargeLine(detail)
        ? (detail.validationMessage || "Error: fuel rate is missing or zero")
        : isOvertimeCutoffValidationLine(detail)
        ? "Error: additional drive time cutoff is missing"
        : isOvertimeLine(detail)
        ? "Additional drive time"
        : isBaseLine(detail)
            ? detail.route?.transactionType?.transactionName
            : detail.surcharge?.description;
    const lineTotal = (detail) => Number(detail?.lineTotal ?? (isOvertimeLine(detail)
        ? (Number(detail?.overtimeAmount ?? 0) || Number(detail?.billRate ?? 0) * Number(detail?.overtimeHours ?? 0))
        : Number(detail?.billRate ?? 0) * Number(detail?.daysOnRoute ?? 0) + Number(detail?.overtimeAmount ?? 0)));
    const lineQuantity = (detail, validationLine, overtimeLine) => {
        if (validationLine) return "Error";
        if (isPercentageSurchargeLine(detail)) return `${Number(detail?.surcharge?.percentage ?? 0).toFixed(2)}%`;
        if (overtimeLine && detail?.route?.roundOvertimeToHalfHour === true) {
            const halfHours = Math.round(Number(detail?.daysOnRoute ?? 0) * 2);
            return `${halfHours} × ½hr`;
        }
        if (isHourlyBaseLine(detail)) {
            return `${Number(detail?.daysOnRoute ?? 0).toFixed(2)} hrs`;
        }
        return Number(detail?.daysOnRoute ?? 0).toFixed(2);
    };
    const getInvoiceRowCategory = (detail) => isBaseLine(detail) ? 0 : (isFalseTripLine(detail) || isBackupDriverLine(detail) || isBackupMakeupLine(detail) || isLeadDriverLine(detail) || isOvertimeLine(detail) || isOvertimeCutoffValidationLine(detail) ? 1 : (isAdjustmentLine(detail) ? 3 : 2));
    const compareInvoiceRows = (a, b) => {
        const aSubCustomer = a.invoice?.subCustomer?.name || `${a.invoice?.subCustomerId ?? ''}`;
        const bSubCustomer = b.invoice?.subCustomer?.name || `${b.invoice?.subCustomerId ?? ''}`;
        if (aSubCustomer !== bSubCustomer) return aSubCustomer.localeCompare(bSubCustomer);

        const aRoute = a.route?.name || '~~~~';
        const bRoute = b.route?.name || '~~~~';
        if (aRoute !== bRoute) return aRoute.localeCompare(bRoute);

        const aCategory = getInvoiceRowCategory(a);
        const bCategory = getInvoiceRowCategory(b);
        if (aCategory !== bCategory) return aCategory - bCategory;

        return (a.id ?? 0) - (b.id ?? 0);
    };

    const sortedFilteredInvoices = useMemo(() => [...filteredInvoices].sort(compareInvoiceRows), [filteredInvoices]);

    const firstDetailIdBySubCustomer = useMemo(() => {
        const map = new Map();
        for (const invoice of sortedFilteredInvoices) {
            const subId = invoice.invoice?.subCustomerId;
            if (subId != null && !map.has(subId)) {
                map.set(subId, invoice.id);
            }
        }
        return map;
    }, [sortedFilteredInvoices]);

    const firstDetailIdBySubCustomerRoute = useMemo(() => {
        const map = new Map();
        for (const invoice of sortedFilteredInvoices) {
            const subId = invoice.invoice?.subCustomerId;
            const routeId = invoice.routeId ?? invoice.route?.id;
            const key = `${subId ?? ''}|${routeId ?? ''}`;
            if (!map.has(key)) {
                map.set(key, invoice.id);
            }
        }
        return map;
    }, [sortedFilteredInvoices]);

    const displayTotalByInvoiceId = useMemo(() => {
        const map = new Map();
        for (const detail of invoices) {
            const invoiceId = detail.invoiceId;
            map.set(invoiceId, (map.get(invoiceId) ?? 0) + roundMoney(lineTotal(detail)));
        }
        return map;
    }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let filtered = [];

        if (!readOnly && subCustomerId) {
            filtered = invoices.filter(i => `${i?.invoice?.subCustomerId}` === subCustomerId);
        } else {
            filtered = [...invoices];
        }

        setFilteredInvoices(filtered);
    }, [invoices, subCustomerId, readOnly])

    useEffect(() => {
        let filters = getFinanceFilters();
        let selected = filters.selectedWeek
            ? weeks.find(w => w.endingDate === filters.selectedWeek)
            : weeks.find(w => w.selected === true);
        if (selected) {
            setSelectedWeek(selected.endingDate);
        }
    }, [weeks]);

    // load existing invoices for prior week on customer change
    useEffect(() => {
        if (readOnly) return;
        const run = async () => {
            var isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/logout');
            }
            else {
                if (!customerId) {
                    setSchedule(null); setInvoices([]);
                    return;
                }
                setLoading(true); setError("");
                try {
                    const token = await Session.getToken();
                    const invoiceDate = selectedWeek ? dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD') : dayjs().subtract(7, 'day').format("YYYY-MM-DD");
                    const [res, subcustomers] = await Promise.all([
                        fetch(loadApi(customerId, invoiceDate), { headers: { Authorization: `Bearer ${token}` } }),
                        fetch(subCustomersApi(customerId), { headers: { Authorization: `Bearer ${token}` } })
                    ]);
                    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                    const data = await res.json();
                    setSchedule(data.schedule);
                    const loadedInvoices = data.invoices || [];
                    setInvoices(loadedInvoices);
                    const loadedSubCustomers = subcustomers.ok ? await subcustomers.json() : [];
                    setSubCustomers(loadedSubCustomers);
                    const dataSubCustomerIds = new Set(loadedInvoices.map(d => `${d.invoice?.subCustomerId}`));
                    if (subCustomerId
                        && !loadedSubCustomers.some(sc => `${sc.id}` === `${subCustomerId}`)
                        && !dataSubCustomerIds.has(`${subCustomerId}`)) {
                        setSubCustomerId('');
                        saveFinanceFilter("subCustomerId", "");
                    }
                } catch (e) {
                    setError(e.message || "Failed to load invoices.");
                } finally {
                    setLoading(false);
                }
            }
        };
        run();
    }, [customerId, navigate, selectedWeek, subCustomerId]);

    // Active sub-customers plus any (possibly inactive) sub-customer that has invoice
    // data for the selected week, so inactive ones with existing data remain selectable.
    const subCustomerOptions = useMemo(() => {
        const map = new Map();
        for (const sc of subCustomers) map.set(`${sc.id}`, sc);
        for (const d of invoices) {
            const sc = d.invoice?.subCustomer;
            if (sc && !map.has(`${sc.id}`)) map.set(`${sc.id}`, sc);
        }
        return [...map.values()];
    }, [subCustomers, invoices]);

    const validateInvoice = (invoice) => {
        let invoiceId = invoice.invoiceId;
        let details = invoices.filter(i => i.invoiceId === invoiceId);
        for (var i = 0; i < details.length; i++) {
            let detail = details[i];
            let mismatch = isFuelSurchargeValidationLine(detail)
                || isZeroCalculatedFuelSurchargeLine(detail)
                || isOvertimeCutoffValidationLine(detail)
                || (isBaseLine(detail) && !isHourlyBaseLine(detail) && detail.daysOnRoute !== detail.expectedDays);
            if (mismatch) {
                return false;
            }
        }

        return true;
    }

    const openSubmit = async (invoice) => {
        setEditing(invoice);
        setSubmitOpen(true);
    }
    const openApprove = (invoice) => {
        setEditing(invoice);
        setPushToQbo(true);
        if (validateInvoice(invoice)) {
            setApproveOpen(true);
        } else {
            setOverrideReason("");
            setOverrideOpen(true);
        }
    };

    // The approve endpoints now return { invoices, qboPreview }. When PreviewInvoicePush is enabled
    // server-side and the user chose to push, qboPreview carries the line-by-line grid to display.
    const applyApproveResponse = (data) => {
        setInvoices(data?.invoices || []);
        if (data?.qboPreview) {
            setQboPreview(data.qboPreview);
            setQboPreviewBlocked(!!data.validationBlocked);
            setQboPreviewOpen(true);
        }
        if (data?.validationBlocked) {
            setError("Invoice NOT approved — one or more line items are not linked to a QuickBooks item. Link the items, then approve again.");
        } else if (data?.qboPushError) {
            setError(`Invoice approved, but the QuickBooks push failed: ${data.qboPushError}`);
            setTimeout(() => setError(""), 8000);
        } else {
            setError("");
        }
    };

    // Combined route notes (paysheet col E + invoicing col F) for the route-row Notes modal.
    const routeNotesText = (line) => [
        line?.paysheetNotes ? `Paysheet notes:\n${line.paysheetNotes}` : null,
        line?.invoicingNotes ? `Invoicing notes:\n${line.invoicingNotes}` : null
    ].filter(Boolean).join("\n\n");

    const handleApproveWithOverride = async () => {
        if (!overrideReason.trim()) return;
        const detail = editing;
        if (!detail) return;
        setOverrideSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(approveOverrideApi(detail.invoiceId, pushToQbo), {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ reason: overrideReason.trim() }),
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            applyApproveResponse(data);
            setOverrideOpen(false);
        } catch (e) {
            setError(e.message || "Failed to approve invoice.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setOverrideSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        const detail = editing;
        if (!detail) { setSubmitOpen(false); setApproveOpen(false); return; }

        setApproveSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(
                submitApi(detail.invoiceId),
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setInvoices(data || []);
            setSubmitOpen(false);
            setApproveOpen(false);
        } catch (e) {
            setError(e.message || "Failed to return invoice to review.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setApproveSubmitting(false);
        }
    }

    const handleApprove = async () => {
        const detail = editing;
        if (!detail) { setApproveOpen(false); return; }

        setApproveSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(
                approveApi(detail.invoiceId, pushToQbo),
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            applyApproveResponse(data);
            setApproveOpen(false);
        } catch (e) {
            setError(e.message || "Failed to approve invoice.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setApproveSubmitting(false);
        }
    }

    const hasExisting = invoices && invoices.length > 0;
    const editingValid = editing ? validateInvoice(editing) : true;

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>{readOnly ? "Approved Invoice (Read-Only)" : "Invoice Audit"}</h5></Col>
                    </Row>
                    {!readOnly && (
                    <Row className="mb-3">
                        <Col md={4}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFinanceFilter("selectedWeek", value);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>
                                        {w.displayDate}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={4}>
                            <SearchableDropdownFilter
                                value={customerId}
                                options={customers}
                                placeholder="Select Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getCustomerLabel}
                                onChange={(value) => {
                                    setCustomerId(value);
                                    setSubCustomerId('');
                                    saveFinanceFilter("customerId", value);
                                    saveFinanceFilter("subCustomerId", "");
                                }}
                            />
                        </Col>
                        <Col md={4}>
                            <SearchableDropdownFilter
                                value={subCustomerId}
                                options={subCustomerOptions}
                                placeholder="Select Sub-Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getSubCustomerLabel}
                                disabled={!customerId}
                                onChange={(value) => {
                                    setSubCustomerId(value);
                                    saveFinanceFilter("subCustomerId", value);
                                }}
                            />
                        </Col>
                    </Row>
                    )}

                    {error && <Alert color="danger">{error}</Alert>}

                    {schedule && (
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Payment Week</div>
                                        <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Invoice Date</div>
                                        <div className="h5 mb-0">{fmt(schedule.invoiceDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <th>Sub-Customer</th>
                                    <th>Route</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Expected</th>
                                    <th>Mileage</th>
                                    <th>Hours</th>
                                    <th>Rate</th>
                                    <th>Line Total</th>
                                    <th>Invoice Total</th>
                                    {!readOnly && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {(!invoices || invoices.length === 0) ? (
                                    <tr><td colSpan={readOnly ? 10 : 11} className="text-center">{readOnly ? "No detail lines found for this invoice." : "No invoices for the prior week."}</td></tr>
                                ) : (
                                    sortedFilteredInvoices.map((invoice, index) => {
                                        const firstRow = firstDetailIdBySubCustomer.get(invoice.invoice?.subCustomerId) === invoice.id;
                                        const firstRouteRow = firstDetailIdBySubCustomerRoute.get(`${invoice.invoice?.subCustomerId ?? ''}|${invoice.routeId ?? invoice.route?.id ?? ''}`) === invoice.id;
                                        const baseLine = isBaseLine(invoice);
                                        const hourlyBase = isHourlyBaseLine(invoice);
                                        const overtimeLine = isOvertimeLine(invoice);
                                        const validationLine = isOvertimeCutoffValidationLine(invoice)
                                            || isFuelSurchargeValidationLine(invoice)
                                            || isZeroCalculatedFuelSurchargeLine(invoice);
                                        const mismatch = validationLine || (baseLine && !hourlyBase && invoice.daysOnRoute !== invoice.expectedDays);
                                        const validated = firstRow ? validateInvoice(invoice) : true;

                                        return (
                                            <React.Fragment key={invoice.id}>
                                                <tr>
                                                    <td>{firstRouteRow ? (invoice.invoice?.subCustomer?.name ?? invoice.invoice?.subCustomerId) : ''}</td>
                                                    <td>
                                                        {firstRouteRow ? invoice.route?.name : ''}
                                                        {firstRouteRow && (invoice.invoicingNotes || invoice.paysheetNotes) && (
                                                            <Button size="sm" color="info" outline className="ms-2" title="View route notes"
                                                                onClick={() => { setNotesText(routeNotesText(invoice)); setNotesOpen(true); }}>
                                                                Notes
                                                            </Button>
                                                        )}
                                                    </td>
                                                    <td>{isFalseTripLine(invoice) ? <Badge color="warning">False Trip</Badge> : isBackupDriverLine(invoice) ? <Badge color="info">Backup Driver</Badge> : isBackupMakeupLine(invoice) ? <Badge color="info">Backup Driver</Badge> : isLeadDriverLine(invoice) ? <Badge color="primary">Lead Pay</Badge> : lineDescription(invoice)}</td>
                                                    <td style={{ textAlign: 'center' }} className={mismatch ? "table-danger fw-bold" : ''}>{lineQuantity(invoice, validationLine, overtimeLine)}</td>
                                                    <td style={{ textAlign: 'center' }}>{baseLine ? (hourlyBase ? "—" : invoice.expectedDays) : ''}</td>
                                                    <td style={{ textAlign: 'center' }}>{invoice.totalMileage ?? 0}</td>
                                                    <td style={{ textAlign: 'center' }}>{Number(invoice.totalHours ?? 0).toFixed(2)}</td>
                                                    <td style={{ textAlign: 'right' }}>${rateMoney(invoice)}</td>
                                                    <td style={{ textAlign: 'right' }}>${money(lineTotal(invoice))}</td>
                                                    <td style={{ textAlign: 'right' }}>{firstRow && `$${money(displayTotalByInvoiceId.get(invoice.invoiceId) ?? 0)}`}</td>
                                                    {!readOnly && (
                                                        <td className="text-center">
                                                            {firstRow && (
                                                                <>
                                                                    <Button size="sm" color={validated ? "success" : "warning"} onClick={() => openApprove(invoice)} style={{marginRight: 5}}>
                                                                        Approve
                                                                    </Button>
                                                                    <Button size="sm" color={validated ? "success" : "warning"} onClick={() => openSubmit(invoice)}>
                                                                        Review
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
            {!readOnly && (
            <>
            <Modal isOpen={approveOpen} toggle={() => !approveSubmitting && setApproveOpen(false)}>
                <ModalHeader toggle={() => !approveSubmitting && setApproveOpen(false)}>Approve Invoice</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>This will approve the selected invoice for billing, do you want to continue?</Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                        <Input
                            type="checkbox"
                            id="approvePushToQbo"
                            checked={pushToQbo}
                            onChange={(e) => setPushToQbo(e.target.checked)}
                            disabled={approveSubmitting}
                        />
                        <Label check for="approvePushToQbo">Push this invoice to QuickBooks</Label>
                    </FormGroup>
                    {approveSubmitting && (
                        <div className="text-muted small mb-2">
                            <Spinner size="sm" /> Approving and uploading to QuickBooks — this can take a minute. Please don't close this window.
                        </div>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setApproveOpen(false)} disabled={approveSubmitting}>Cancel</Button>
                        <Button color="warning" onClick={handleSubmit} disabled={approveSubmitting}>Send to Review</Button>
                        <Button color="primary" onClick={handleApprove} disabled={approveSubmitting}>
                            {approveSubmitting ? <><Spinner size="sm" /> Approving…</> : "Approve"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
            <Modal isOpen={submitOpen} toggle={() => setSubmitOpen(false)}>
                <ModalHeader toggle={() => setSubmitOpen(false)}>Return to review</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Alert color="warning" className="mb-0">
                            This will send the invoice back to be reviewed.  Do you want to continue?
                        </Alert>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSubmitOpen(false)}>Cancel</Button>
                        <Button color="warning" onClick={handleSubmit}>Send to Review</Button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Validation Override Modal — Approve */}
            <Modal isOpen={overrideOpen} toggle={() => !overrideSubmitting && setOverrideOpen(false)} size="lg">
                <ModalHeader toggle={() => !overrideSubmitting && setOverrideOpen(false)}>
                    Approve Invoice — Validation Override
                </ModalHeader>
                <ModalBody>
                    <Alert color="warning">
                        <strong>This invoice has one or more validation errors.</strong> Approving it will bypass those
                        checks. An audit record will be saved with your name, the timestamp, and the reason you provide below.
                    </Alert>
                    {editing && (() => {
                        const issues = invoices
                            .filter(d => d.invoiceId === editing.invoiceId)
                            .reduce((acc, d) => {
                                if (isFuelSurchargeValidationLine(d) || isZeroCalculatedFuelSurchargeLine(d))
                                    acc.add("Fuel surcharge rate is missing or zero for one or more routes.");
                                if (isOvertimeCutoffValidationLine(d))
                                    acc.add("Additional drive time cutoff is not configured for one or more routes.");
                                if (isBaseLine(d) && !isHourlyBaseLine(d) && d.daysOnRoute !== d.expectedDays)
                                    acc.add("Actual days on route do not match expected days for one or more routes.");
                                return acc;
                            }, new Set());
                        return issues.size > 0 ? (
                            <div className="mb-3">
                                <div className="fw-bold mb-1">Validation issues found:</div>
                                <ul className="mb-0">
                                    {[...issues].map((msg, i) => <li key={i}>{msg}</li>)}
                                </ul>
                            </div>
                        ) : null;
                    })()}
                    <FormGroup>
                        <Label for="overrideReasonInput">
                            Reason for overriding validation <span className="text-danger">*</span>
                        </Label>
                        <Input
                            id="overrideReasonInput"
                            type="textarea"
                            rows={4}
                            maxLength={2000}
                            placeholder="Describe why this invoice is being approved despite validation errors..."
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            disabled={overrideSubmitting}
                        />
                        <div className="text-muted small mt-1">{overrideReason.length} / 2000</div>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                        <Input
                            type="checkbox"
                            id="approveOverridePushToQbo"
                            checked={pushToQbo}
                            onChange={(e) => setPushToQbo(e.target.checked)}
                            disabled={overrideSubmitting}
                        />
                        <Label check for="approveOverridePushToQbo">Push this invoice to QuickBooks</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setOverrideOpen(false)} disabled={overrideSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            onClick={handleApproveWithOverride}
                            disabled={!overrideReason.trim() || overrideSubmitting}
                        >
                            {overrideSubmitting ? "Approving…" : "Override & Approve"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            {/* QuickBooks Push Preview (grid) — shown instead of pushing when PreviewInvoicePush is on */}
            <Modal isOpen={qboPreviewOpen} toggle={() => setQboPreviewOpen(false)} size="lg">
                <ModalHeader toggle={() => setQboPreviewOpen(false)}>
                    {qboPreviewBlocked ? "Cannot Push to QuickBooks" : "QuickBooks Invoice Preview"}
                    {qboPreview?.docNumber ? ` — #${qboPreview.docNumber}` : ""}
                </ModalHeader>
                <ModalBody>
                    {qboPreviewBlocked ? (
                        <Alert color="danger" className="mb-3">
                            This invoice was <strong>not approved or pushed</strong> because one or more line items
                            are not linked to a QuickBooks item. Link the highlighted items, then approve again.
                        </Alert>
                    ) : (
                        <Alert color="info" className="mb-3">
                            Preview mode is enabled, so this invoice was <strong>not</strong> pushed to QuickBooks.
                            Below is the line-by-line data that would be sent.
                        </Alert>
                    )}
                    {qboPreview && (
                        <>
                            <Row className="mb-3">
                                <Col md={6}><div className="text-muted small">Customer</div><div>{qboPreview.customerName || "—"}</div></Col>
                                <Col md={3}><div className="text-muted small">Txn Date</div><div>{qboPreview.txnDate || "—"}</div></Col>
                                <Col md={3}><div className="text-muted small">Service Date</div><div>{qboPreview.serviceDate || "—"}</div></Col>
                            </Row>
                            {qboPreview.warnings && qboPreview.warnings.length > 0 && (
                                <Alert color="warning">
                                    <div className="fw-bold mb-1">Issues that would affect a live push:</div>
                                    <ul className="mb-0">
                                        {qboPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                    </ul>
                                </Alert>
                            )}
                            <Table bordered size="sm" responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th>QB Item</th>
                                        <th className="text-center">Qty</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(qboPreview.lines || []).map((l, i) => (
                                        <tr key={i}>
                                            <td>{l.description}</td>
                                            <td>{l.itemName || <span className="text-danger">— not linked —</span>}</td>
                                            <td className="text-center">{Number(l.qty ?? 0)}</td>
                                            <td className="text-end">${money(l.unitPrice)}</td>
                                            <td className="text-end">${money(l.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold">Total</td>
                                        <td className="text-end fw-bold">${money(qboPreview.total)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}
                    <div className="d-flex justify-content-end">
                        <Button color="primary" onClick={() => setQboPreviewOpen(false)}>Close</Button>
                    </div>
                </ModalBody>
            </Modal>
            </>
            )}

            <Modal isOpen={notesOpen} toggle={() => setNotesOpen(false)}>
                <ModalHeader toggle={() => setNotesOpen(false)}>Route Notes</ModalHeader>
                <ModalBody>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{notesText || "(no notes)"}</div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={() => setNotesOpen(false)}>Close</Button>
                    </div>
                </ModalBody>
            </Modal>

        </Container>
    );
};

export default InvoiceAudit;
